import { useState, useEffect } from 'react';
import {
  Box, Typography, Stack, Button, CircularProgress, Paper, Chip,
  Select, MenuItem, FormControl, InputLabel, Dialog, DialogTitle,
  DialogContent, DialogActions,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import {
  AccountBalanceWallet as AsientoIcon, CheckCircle as ContabIcon,
  Cancel as AnularIcon, Visibility as ViewIcon, Savings as ProvisionIcon,
} from '@mui/icons-material';
import { AsientosService } from '../services/asientosService';
import { PlanillonService } from '../../planillon/services/planillonService';
import type { AsientoContable } from '../types/asientos';
import type { PlanillaCabecera } from '../../planillon/types/planillon';

const fmt = (n: number) => `S/ ${n.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AsientosPage() {
  const [planillas, setPlanillas] = useState<PlanillaCabecera[]>([]);
  const [selectedPlanilla, setSelectedPlanilla] = useState<number>(0);
  const [asientos, setAsientos] = useState<AsientoContable[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedAsiento, setSelectedAsiento] = useState<AsientoContable | null>(null);

  useEffect(() => { loadPlanillas(); }, []);

  const loadPlanillas = async () => {
    try {
      const data = await PlanillonService.getAll();
      const valid = data.filter(p => p.estado !== 'BORRADOR');
      setPlanillas(valid);
      if (valid.length > 0) setSelectedPlanilla(valid[0].idPlanilla);
    } catch { /* handled */ }
  };

  const loadAsientos = async () => {
    if (!selectedPlanilla) return;
    setLoading(true);
    try { setAsientos(await AsientosService.getByPlanilla(selectedPlanilla)); }
    catch { /* handled */ } finally { setLoading(false); }
  };

  useEffect(() => { if (selectedPlanilla) loadAsientos(); }, [selectedPlanilla]);

  const handleGenerar = async () => {
    if (!selectedPlanilla) return;
    setLoading(true);
    try { await AsientosService.generar(selectedPlanilla); await loadAsientos(); }
    catch { /* handled */ } finally { setLoading(false); }
  };

  const handleGenerarProvision = async () => {
    if (!selectedPlanilla) return;
    setLoading(true);
    try { await AsientosService.generarProvision(selectedPlanilla); await loadAsientos(); }
    catch { /* handled */ } finally { setLoading(false); }
  };

  const handleContabilizar = async (id: number) => {
    try { await AsientosService.contabilizar(id); await loadAsientos(); } catch { /* handled */ }
  };

  const handleAnular = async (id: number) => {
    try { await AsientosService.anular(id); await loadAsientos(); } catch { /* handled */ }
  };

  const handleViewDetail = async (id: number) => {
    try { setSelectedAsiento(await AsientosService.getById(id)); setDetailDialog(true); } catch { /* handled */ }
  };

  const columns: GridColDef[] = [
    { field: 'numeroAsiento', headerName: 'N° Asiento', width: 180 },
    { field: 'tipoAsiento', headerName: 'Tipo', width: 120 },
    { field: 'fechaAsiento', headerName: 'Fecha', width: 110, valueFormatter: (v) => v ? new Date(v).toLocaleDateString('es-PE') : '' },
    { field: 'glosa', headerName: 'Glosa', flex: 1, minWidth: 200 },
    { field: 'totalDebe', headerName: 'Debe', width: 130, valueFormatter: (v) => fmt(v) },
    { field: 'totalHaber', headerName: 'Haber', width: 130, valueFormatter: (v) => fmt(v) },
    { field: 'estado', headerName: 'Estado', width: 130,
      renderCell: (p) => <Chip label={p.value} size="small" color={p.value === 'CONTABILIZADO' ? 'success' : p.value === 'ANULADO' ? 'error' : 'warning'} /> },
    { field: 'acciones', headerName: '', width: 160, sortable: false,
      renderCell: (p) => (
        <Stack direction="row" spacing={0.5}>
          <Button size="small" onClick={() => handleViewDetail(p.row.idAsiento)}><ViewIcon fontSize="small" /></Button>
          {p.row.estado === 'GENERADO' && (
            <>
              <Button size="small" color="success" onClick={() => handleContabilizar(p.row.idAsiento)}><ContabIcon fontSize="small" /></Button>
              <Button size="small" color="error" onClick={() => handleAnular(p.row.idAsiento)}><AnularIcon fontSize="small" /></Button>
            </>
          )}
        </Stack>
      )},
  ];

  const detalleColumns: GridColDef[] = [
    { field: 'cuentaContable', headerName: 'Cuenta', width: 120 },
    { field: 'descripcionCuenta', headerName: 'Descripción', flex: 1, minWidth: 200 },
    { field: 'centroCosto', headerName: 'CC', width: 100 },
    { field: 'debe', headerName: 'Debe', width: 130, valueFormatter: (v) => fmt(v) },
    { field: 'haber', headerName: 'Haber', width: 130, valueFormatter: (v) => fmt(v) },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>Asientos Contables</Typography>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 350 }}>
          <InputLabel>Planilla</InputLabel>
          <Select value={selectedPlanilla || ''} label="Planilla"
            onChange={(e) => setSelectedPlanilla(Number(e.target.value))}>
            {planillas.map(p => <MenuItem key={p.idPlanilla} value={p.idPlanilla}>{p.descripcion} - {p.estado}</MenuItem>)}
          </Select>
        </FormControl>
        <Button variant="contained" startIcon={loading ? <CircularProgress size={18} /> : <AsientoIcon />}
          onClick={handleGenerar} disabled={loading || !selectedPlanilla}>Generar Asiento</Button>
        <Button variant="contained" color="warning" startIcon={loading ? <CircularProgress size={18} /> : <ProvisionIcon />}
          onClick={handleGenerarProvision} disabled={loading || !selectedPlanilla}>Generar Provisiones</Button>
      </Stack>

      <DataGrid rows={asientos} columns={columns} getRowId={(row) => row.idAsiento} autoHeight density="compact"
        disableRowSelectionOnClick pageSizeOptions={[10, 25]} initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} />

      <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Detalle: {selectedAsiento?.numeroAsiento}</DialogTitle>
        <DialogContent>
          {selectedAsiento && (
            <>
              <Paper sx={{ p: 2, mb: 2 }}>
                <Stack direction="row" spacing={4}>
                  <Box><Typography variant="body2" color="text.secondary">Glosa</Typography><Typography>{selectedAsiento.glosa}</Typography></Box>
                  <Box><Typography variant="body2" color="text.secondary">Total Debe</Typography><Typography variant="h6" color="error.main">{fmt(selectedAsiento.totalDebe)}</Typography></Box>
                  <Box><Typography variant="body2" color="text.secondary">Total Haber</Typography><Typography variant="h6" color="success.main">{fmt(selectedAsiento.totalHaber)}</Typography></Box>
                </Stack>
              </Paper>
              <DataGrid rows={selectedAsiento.detalles.map((d, i) => ({ ...d, _id: i }))} columns={detalleColumns}
                getRowId={(row) => row._id} autoHeight density="compact" disableRowSelectionOnClick />
            </>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDetailDialog(false)}>Cerrar</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
